import { getProductDetailsDirect, updateStockItem } from '../clients/product_client';
import { listStorageNodeContents } from '../clients/storage_client';
import { logError, logInfo } from '../config_and_helpers/logger';
import { findNode, readSlotCount } from './storageTree';
import { planLotMoves, stockItemFieldsForPlace } from './storagePlace';

const LOG_CONTEXT = 'moveStockItems';

/**
 * Move one or more lots to a place. Several lots into a slotted box take the
 * next free slots. Each lot is saved with its current batch, in-use, expiry,
 * and opened-on. A shortfall of slots saves nothing.
 *
 * @param {Object} args
 * @param {string} args.token
 * @param {Array} args.nodes
 * @param {Array<{ id: string|number, product_id?: string|number, cat_num?: string, batch?: string }>} args.lots
 * @param {string|number|null} args.storageNodeId
 * @param {string|number|null} args.slotIndex
 * @returns {Promise<{ error: string|null, moved: Array, failed: Array }>}
 */
export async function moveSelectedLots({
  token,
  nodes,
  lots,
  storageNodeId,
  slotIndex,
}) {
  const selected = (lots || []).filter(lot => lot && lot.id != null);
  const place = findNode(nodes, storageNodeId);
  let occupied = [];
  if (place && readSlotCount(place) && selected.length > 1) {
    const contents = await listStorageNodeContents(token, place.id);
    const moving = new Set(selected.map(lot => String(lot.id)));
    occupied = (contents.success ? contents.data || [] : [])
      .filter(item => item && !moving.has(String(item.id)))
      .map(item => item.slot_index);
  }
  const plan = planLotMoves(nodes, storageNodeId, slotIndex, selected.length, occupied);
  if (plan.error) {
    logInfo(LOG_CONTEXT, 'move refused before save', {
      lotCount: selected.length,
      storageNodeId: storageNodeId ?? null,
      error: plan.error,
    });
    return { error: plan.error, moved: [], failed: selected };
  }

  const byProduct = new Map();
  selected.forEach((lot, index) => {
    const key = String(lot.product_id ?? '');
    const group = byProduct.get(key) || [];
    group.push({ lot, slot: plan.slots[index] });
    byProduct.set(key, group);
  });

  const moved = [];
  const failed = [];
  for (const [productId, group] of byProduct) {
    const product = productId
      ? await getProductDetailsDirect(token, productId)
      : null;
    const items = Array.isArray(product?.items) ? product.items : [];
    for (const entry of group) {
      const detail = items.find(item => String(item.id) === String(entry.lot.id));
      if (!detail) {
        logError(LOG_CONTEXT, 'lot missing from product detail', {
          stockItemId: entry.lot.id,
          productId: productId || null,
        });
        failed.push(entry.lot);
        continue;
      }
      const result = await updateStockItem(token, detail.id, {
        ...stockItemFieldsForPlace(detail),
        storageNodeId: plan.storageNodeId,
        slotIndex: entry.slot,
      });
      if (!result?.success) {
        const message = Array.isArray(result) ? String(result.flat()[0] ?? '') : '';
        logError(LOG_CONTEXT, 'lot move rejected', {
          stockItemId: detail.id,
          message: message || null,
        });
        failed.push({ ...entry.lot, message });
      } else {
        moved.push(entry.lot);
        logInfo(LOG_CONTEXT, 'lot moved', {
          stockItemId: detail.id,
          storageNodeId: plan.storageNodeId,
          slotIndex: entry.slot,
        });
      }
    }
  }
  return { error: null, moved, failed };
}
